import * as React from 'react';
import { useTranslation } from '../../../providers/UIProvider';
import type { ValidationIssue } from '../../../types';
import { EditableFieldDisplay } from './EditableFieldDisplay';
import { EditableFieldEditor } from './EditableFieldEditor';
import { cn } from '../../../utils';

export interface EditableFieldProps<TValue = string> {
  labelSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** External (final) value – controlled prop */
  value: TValue;

  /** Callback after saving new value */
  onSave: (value: TValue) => void | Promise<void>;

  /**
   * Render prop for custom edit input.
   * Returns e.g. <input>, <textarea> or custom component.
   *
   * `onCommit(val)` immediately saves the given value (bypassing the Save button)
   * and exits edit mode. Useful for editors with their own commit gesture, like
   * a native date picker. The returned promise rejects when `onSave` rejects so
   * modal-based editors can surface the failure (banner, retry, …) and keep
   * themselves open instead of closing on a failed save.
   */
  render: (params: {
    value: TValue;
    onChange: (val: TValue) => void;
    onCommit: (val: TValue) => Promise<void>;
    onCancel: () => void;
    onBlur: () => void;
    errors?: ValidationIssue[];
  }) => React.ReactNode;

  /**
   * Optional: custom renderer for display mode (editing = false).
   * Default is <span>{value}</span>. The current `errors` are passed alongside
   * the value so formatters can render issue-level detail (per-row icons,
   * inline warnings, …) when needed.
   */
  formatter?: (value: TValue, errors?: ValidationIssue[]) => React.ReactNode;

  /**
   * Optional: placeholder text to display when value is empty/null/undefined.
   * Rendered with foreground-muted styling.
   * @default '—'
   */
  placeholder?: string;

  className?: string;

  /**
   * Optional: disabled mode – prevents editing
   */
  disabled?: boolean;

  /**
   * Optional: display mode – 'inline' (default) or 'block'
   * @default 'inline'
   */
  display?: 'inline' | 'block';

  /**
   * Accessible label for the confirm/save icon button.
   * Falls back to the ui-toolkit translation `components.editableField.save`.
   */
  saveLabel?: string;

  /**
   * Accessible label for the cancel icon button.
   * Falls back to the ui-toolkit translation `components.editableField.cancel`.
   */
  cancelLabel?: string;

  /**
   * Optional: validation issues to surface on the field. When provided in
   * display mode, the field gets a semantic border (worst severity wins), a
   * severity icon replaces the edit pen, and the icon's tooltip shows the
   * combined issue messages joined with ' · '. The full array is also
   * forwarded to the `formatter` so it can render issue-level detail.
   */
  errors?: ValidationIssue[];

  /**
   * Hide the floating save/cancel toolbar in edit mode. Use when the editor
   * commits itself via `onCommit` (e.g. native date pickers, selects).
   * @default false
   */
  hideActions?: boolean;

  rounded?: 'none' | 'md' | '2xl';
  showErrors?: boolean;
  bordered?: boolean;

  /**
   * When true, focusing the display button (via keyboard `Tab` or programmatic
   * focus from the consumer) activates edit mode automatically — no extra
   * Enter/Space keystroke required. Programmatic focus restored by this
   * component after save/cancel is suppressed so we never bounce straight back
   * into edit mode.
   *
   * Moving focus out of the open editor (e.g. `Tab`) saves a changed value, or just closes it.
   *
   * Recommended only for lightweight inline editors (text, date, decimal,
   * single-select). Avoid for modal-based editors (invoice lines, VAT
   * summary, address) where auto-opening would be disruptive during simple
   * keyboard navigation.
   *
   * @default false
   */
  openOnFocus?: boolean;

  /**
   * Optional controlled editing flag. When provided, the parent owns the
   * open/closed state — typical use is when a wrapping row drives editing via
   * its own focus management. In controlled mode the component skips its
   * internal focus-restoration to the display button (the parent decides
   * where focus lands) and the display button is taken out of the tab order
   * so only the parent is focusable.
   */
  editing?: boolean;

  /**
   * Notify the parent when the editor wants to open or close. Always fires,
   * whether `editing` is controlled or not, so parents can react to inner
   * gestures (Save/Cancel, Escape, click outside).
   */
  onEditingChange?: (editing: boolean) => void;
  /** Apply input-like styling in display mode — makes the field look active/editable at all times. */
  alwaysActive?: boolean;
  /**
   * `ArrowDown` / `ArrowUp` on the display open the editor, as on a closed select. For pickers
   * that open their menu on mount (`defaultOpen`).
   * @default false
   */
  openOnArrowKeys?: boolean;
}

export function EditableField<TValue>({
  className,
  value: externalValue,
  onSave,
  render,
  formatter,
  placeholder = '—',
  disabled = false,
  display = 'inline',
  saveLabel,
  cancelLabel,
  errors,
  hideActions = false,
  rounded = 'md',
  showErrors = true,
  openOnFocus = false,
  editing: controlledEditing,
  onEditingChange,
  bordered = true,
  alwaysActive = false,
  openOnArrowKeys = false
}: EditableFieldProps<TValue>) {
  const t = useTranslation('components.editableField');
  const resolvedSaveLabel = saveLabel ?? t('save');
  const resolvedCancelLabel = cancelLabel ?? t('cancel');
  const isControlled = controlledEditing !== undefined;
  const [internalEditing, setInternalEditing] = React.useState(false);
  const editing = isControlled ? controlledEditing : internalEditing;
  // Mirrors `editing` for callbacks running from stale closures (e.g. a
  // modal's async save handler captured before the commit closed the editor).
  // Updated synchronously in `setEditing` — the React state update hasn't
  // flushed yet when such a callback fires right after an awaited commit.
  const editingRef = React.useRef(editing);
  editingRef.current = editing;
  const setEditing = React.useCallback(
    (next: boolean) => {
      editingRef.current = next;
      if (!isControlled) setInternalEditing(next);
      onEditingChange?.(next);
    },
    [isControlled, onEditingChange]
  );
  const [saving, setSaving] = React.useState(false);
  // Set when the user opened the editor, so it may take focus; never for a parent's `editing`.
  const [openedByUser, setOpenedByUser] = React.useState(false);
  const openByUser = React.useCallback(() => {
    setOpenedByUser(true);
    setEditing(true);
  }, [setEditing]);
  const [value, setValue] = React.useState(externalValue);
  const wasEditingRef = React.useRef(false);
  const displayButtonRef = React.useRef<HTMLDivElement>(null);
  // Flag set just before we programmatically focus the display button so the
  // ensuing `onFocus` doesn't bounce straight back into edit mode when
  // `openOnFocus` is on.
  const suppressOpenOnFocusRef = React.useRef(false);

  const buttonClassName = cn(
    rounded === 'none' ? 'rounded-none' : rounded === 'md' ? 'rounded-md' : 'rounded-2xl'
  );

  // Set when the parent value changes while editing; applied once the editor closes.
  const externalChangedWhileEditingRef = React.useRef(false);
  const lastExternalValueRef = React.useRef(externalValue);

  // When parent value changes and we're not editing → sync.
  // `editing` is intentionally omitted from deps: we don't want a reset when
  // the editor closes after a successful save (Apollo hasn't updated
  // externalValue yet at that point). handleCancel resets explicitly.
  React.useEffect(() => {
    if (Object.is(lastExternalValueRef.current, externalValue)) return;
    lastExternalValueRef.current = externalValue;
    if (editing) externalChangedWhileEditingRef.current = true;
    else setValue(externalValue);
  }, [externalValue]); // intentional: editing omitted

  // After edit mode exits: apply a parent value that arrived while editing, then
  // send focus back to the display button so keyboard users can continue Tab-ing.
  // Focus is skipped when disabled — a visually disabled editor must not grab focus —
  // and when it already moved elsewhere (another field opened while this one saved).
  React.useEffect(() => {
    if (wasEditingRef.current && !editing) {
      setOpenedByUser(false);
      if (externalChangedWhileEditingRef.current) {
        externalChangedWhileEditingRef.current = false;
        setValue(externalValue);
      }
      const el = displayButtonRef.current;
      const active = document.activeElement;
      const focusLost = !active || active === document.body;
      if (el && !disabled && focusLost) {
        suppressOpenOnFocusRef.current = true;
        el.focus();
      }
    }
    wasEditingRef.current = editing;
  }, [editing]);

  const commit = async (valToSave: TValue): Promise<void> => {
    setSaving(true);
    try {
      await onSave(valToSave);
      // Update local state before closing so the display shows the saved value
      // immediately, before Apollo has had a chance to update externalValue.
      setValue(valToSave);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  // Save-button flow: swallow errors so the user stays in edit mode and can
  // retry / correct the value without surfacing an unhandled rejection.
  const persist = (valToSave: TValue): Promise<void> => commit(valToSave).catch(() => {});

  // Commit flow (used by editors that bypass the Save toolbar — e.g. modals,
  // native date pickers): propagate rejection so the editor can keep itself
  // open and show a save error.
  const handleCommit = async (newVal: TValue): Promise<void> => {
    setValue(newVal);
    await commit(newVal);
  };

  const handleCancel = React.useCallback(() => {
    // Modal editors fire onCancel from their close handler even after a
    // successful save. By then editing is already false — resetting here
    // would stomp the freshly committed value with a stale externalValue.
    if (!editingRef.current) return;
    externalChangedWhileEditingRef.current = false;
    setValue(externalValue);
    setEditing(false);
  }, [externalValue, setEditing]);

  const handleDisplayFocus = React.useCallback(() => {
    if (suppressOpenOnFocusRef.current) {
      suppressOpenOnFocusRef.current = false;
      return;
    }
    if (openOnFocus && !disabled) openByUser();
  }, [openOnFocus, disabled, openByUser]);

  if (editing) {
    return (
      <EditableFieldEditor
        value={value}
        saving={saving}
        saveLabel={resolvedSaveLabel}
        cancelLabel={resolvedCancelLabel}
        render={render}
        errors={errors}
        onChange={setValue}
        onCommit={handleCommit}
        onSave={() => void persist(value)}
        onCancel={handleCancel}
        hideActions={hideActions}
        focusOnMount={openedByUser}
        onFocusLeave={
          openOnFocus
            ? () => (Object.is(value, externalValue) ? handleCancel() : void persist(value))
            : // A self-committing picker left unpicked closes, as a click outside it would.
              hideActions && !isControlled
              ? handleCancel
              : undefined
        }
      />
    );
  }

  return (
    <EditableFieldDisplay
      bordered={bordered}
      alwaysActive={alwaysActive}
      value={value}
      formatter={formatter}
      placeholder={placeholder}
      disabled={disabled}
      display={display}
      errors={errors ?? []}
      onActivate={openByUser}
      openOnArrowKeys={openOnArrowKeys}
      onFocus={handleDisplayFocus}
      buttonRef={displayButtonRef}
      className={className}
      buttonClassName={buttonClassName}
      showErrors={showErrors}
    />
  );
}

EditableField.displayName = 'EditableField';
